import { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Camera, FileText, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { EXPENSE_CATEGORIES, EXPENSE_CAT_LABEL } from "@/lib/expenseCategories";
import { ExpenseDateFilter, expenseFilterRange } from "@/components/ExpenseDateFilter";

function fmtBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function UploadDialog({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    category: "FOOD_PURCHASES", vendor: "", purchase_date: "",
    amount_nzd: "", gst_nzd: "", description: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setForm({ category: "FOOD_PURCHASES", vendor: "", purchase_date: "", amount_nzd: "", gst_nzd: "", description: "" });
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Attach or capture the receipt"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large — max 25 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", form.category);
    if (form.vendor) fd.append("vendor", form.vendor);
    if (form.purchase_date) fd.append("purchase_date", form.purchase_date);
    if (form.amount_nzd) fd.append("amount_nzd", form.amount_nzd);
    if (form.gst_nzd) fd.append("gst_nzd", form.gst_nzd);
    if (form.description) fd.append("description", form.description);
    try {
      await api.post("/manager/expenses/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Expense saved");
      reset(); setOpen(false); onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#1A2744] text-white" data-testid="expense-upload-btn">
          <Plus className="h-4 w-4 mr-1" /> New expense
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Upload expense receipt</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="expense-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Vendor</Label>
              <Input value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} placeholder="e.g. PaknSave" data-testid="expense-vendor" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.purchase_date} onChange={(e) => setForm((f) => ({ ...f, purchase_date: e.target.value }))} data-testid="expense-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Amount (NZD)</Label>
              <Input type="number" step="0.01" value={form.amount_nzd} onChange={(e) => setForm((f) => ({ ...f, amount_nzd: e.target.value }))} data-testid="expense-amount" />
            </div>
            <div>
              <Label>GST (NZD)</Label>
              <Input type="number" step="0.01" value={form.gst_nzd} onChange={(e) => setForm((f) => ({ ...f, gst_nzd: e.target.value }))} data-testid="expense-gst" />
            </div>
          </div>
          <div>
            <Label>Description / what for</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="expense-desc" />
          </div>
          <div className="space-y-2">
            <Label>Receipt (PDF or photo)</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="flex-1">
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="expense-file" />
                <span className="cursor-pointer flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  <FileText className="h-4 w-4" /> Choose file
                </span>
              </label>
              <label className="flex-1">
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="expense-camera" />
                <span className="cursor-pointer flex items-center justify-center gap-2 px-3 py-2 border border-[#1A2744] text-[#1A2744] rounded-lg text-sm hover:bg-[#1A2744]/5">
                  <Camera className="h-4 w-4" /> Take photo
                </span>
              </label>
            </div>
            {file && <div className="text-xs text-gray-600">{file.name} — {fmtBytes(file.size)}</div>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#1A2744] text-white" data-testid="expense-submit">
              {busy ? "Uploading…" : "Save expense"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Shared "my expense receipts" panel — used by both the employee Expenses
    page and the manager Expenses page (same /manager/expenses endpoints). */
export function ExpenseReceipts() {
  const [rows, setRows] = useState([]);
  const [dateFilter, setDateFilter] = useState({ mode: "ALL", date: "", month: "", year: String(new Date().getFullYear()) });
  const [catFilter, setCatFilter] = useState("ALL");

  const range = useMemo(() => expenseFilterRange(dateFilter), [dateFilter]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (range) { params.start = range.start; params.end = range.end; }
      if (catFilter !== "ALL") params.category = catFilter;
      const { data } = await api.get("/manager/expenses", { params });
      setRows(data.expenses || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, [range?.start, range?.end, catFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce(
    (acc, r) => { acc.amount += Number(r.amount_nzd || 0); acc.gst += Number(r.gst_nzd || 0); return acc; },
    { amount: 0, gst: 0 }
  );

  const download = (row) => {
    window.open(`${API_BASE}/manager/expenses/${row.id}/download`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="expense-receipts-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <UploadDialog onUploaded={load} />
        <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
          <Filter className="h-4 w-4 text-gray-500" />
          <ExpenseDateFilter value={dateFilter} onChange={setDateFilter} idPrefix="my-expense-filter" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-[170px] h-9" data-testid="my-expense-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Receipts</div>
          <div className="text-2xl font-bold text-[#1A2744]" data-testid="my-expense-count">{rows.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Total (NZD)</div>
          <div className="text-2xl font-bold text-[#1A2744]" data-testid="my-expense-total">${totals.amount.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Total GST (NZD)</div>
          <div className="text-2xl font-bold text-[#C9A84C]" data-testid="my-expense-gst">${totals.gst.toFixed(2)}</div>
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Category</TableHead>
            <TableHead>Vendor</TableHead><TableHead>Amount</TableHead>
            <TableHead>GST</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-6">No expenses in this period</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell className="text-xs">{r.purchase_date || r.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-xs">{EXPENSE_CAT_LABEL[r.category] || r.category}</TableCell>
                <TableCell className="text-xs">{r.vendor || "—"}</TableCell>
                <TableCell className="text-xs">{r.amount_nzd != null ? `$${Number(r.amount_nzd).toFixed(2)}` : "—"}</TableCell>
                <TableCell className="text-xs">{r.gst_nzd != null ? `$${Number(r.gst_nzd).toFixed(2)}` : "—"}</TableCell>
                <TableCell className="text-xs"><span className={`px-2 py-0.5 rounded font-mono text-[10px] ${r.status === "REVIEWED" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</span></TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`expense-download-${r.id}`}>
                    <Download className="h-3 w-3 mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
